<template>
  <v-progress-linear v-if="loading" indeterminate />
  <v-alert v-else-if="loadError" type="error" variant="tonal" class="mb-2">
    {{ loadError }}
    <template #append>
      <v-btn variant="text" @click="loadAgreements">Retry</v-btn>
    </template>
  </v-alert>
  <v-form v-else>
    <v-checkbox
      v-for="status in statuses"
      :key="status.agreement.assignId"
      :label="
        status.latestInstance.agreementAgreeText ??
        status.agreement.agreementName ??
        undefined
      "
      :model-value="status.isAgreed"
      :disabled="saving"
      hide-details="auto"
      @update:model-value="
        (checked) => (checked ? (openAgreement = status) : sign(status, false))
      "
    />
    <v-alert v-if="saveError" type="error" variant="tonal" class="mt-2">
      {{ saveError }}
    </v-alert>
  </v-form>
  <v-dialog
    :model-value="!!openAgreement"
    max-width="600"
    @update:model-value="(open) => !open && (openAgreement = undefined)"
  >
    <v-card v-if="openAgreement">
      <v-card-title class="headline">{{
        openAgreement.latestInstance.agreementHeader ??
        openAgreement.agreement.agreementName
      }}</v-card-title>
      <v-card-text class="agreement-text">
        {{ openAgreement.latestInstance.agreementText }}
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="openAgreement = undefined">Close</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="saving"
          @click="sign(openAgreement, true)"
          >Agree</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script lang="ts" setup>
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import { useMemberService } from "../../../cbi-generator/frontend/composables/useMemberFrontendService";
import type { MemberAgreementStatus } from "../../../cbi-generator/generated/dto/MemberAgreementStatus";
import { errorMessage } from "@/composables/generated/contracts";

const props = defineProps<{
  /** Member's own online order whose items determine required agreements. */
  orderId?: number | null;
  /** Explicitly required agreements (e.g. kiosk registration links). */
  agreementIds?: number[] | null;
}>();
const emit = defineEmits<{ termsAgreedUpdated: [allAgreed: boolean] }>();

const service = useMemberService();
const loading = ref(true);
const saving = ref(false);
const loadError = ref("");
const saveError = ref("");
const statuses = shallowRef<MemberAgreementStatus[]>([]);
const openAgreement = shallowRef<MemberAgreementStatus>();
const allAgreed = computed(
  () =>
    !loading.value &&
    !loadError.value &&
    statuses.value.every((s) => s.isAgreed),
);
let version = 0;

async function loadAgreements() {
  const current = ++version;
  loading.value = true;
  loadError.value = "";
  try {
    const result = await service.requiredAgreementsMember({
      order: props.orderId ? { assignId: props.orderId } : null,
      agreements: props.agreementIds
        ? props.agreementIds.map((assignId) => ({ assignId }))
        : null,
    });
    if (current !== version) return;
    statuses.value = result.items;
  } catch (e) {
    if (current !== version) return;
    statuses.value = [];
    loadError.value = errorMessage(e);
  } finally {
    if (current === version) loading.value = false;
  }
}

async function sign(status: MemberAgreementStatus, agree: boolean) {
  saving.value = true;
  saveError.value = "";
  try {
    const updated = await service.signAgreementMember({
      agreement: { assignId: status.agreement.assignId },
      agree,
    });
    statuses.value = statuses.value.map((s) =>
      s.agreement.assignId === updated.agreement.assignId ? updated : s,
    );
    openAgreement.value = undefined;
  } catch (e) {
    saveError.value = errorMessage(e);
  } finally {
    saving.value = false;
  }
}

watch(allAgreed, (agreed) => emit("termsAgreedUpdated", agreed), {
  immediate: true,
});
watch(() => [props.orderId, props.agreementIds?.join(",")], loadAgreements);
onMounted(loadAgreements);
</script>
<style scoped>
.agreement-text {
  white-space: pre-wrap;
}
</style>
